<template>
  <div class="field">
    <label class="field__label" :for="name">{{ label }}</label>
    <div class="dropzone field__input" ref="dropzone" :class="{ dropped: isOverDropZone }">
      <div class="upload__inner">
        <label class="field__label--inside">{{ label }}</label>
        <p class="mb-1 mt-1" v-if="files?.length <= 0">
          Sleep bestanden hierheen of
        </p>
        <div class="upload__holder mb-1 mt-1" v-else>
          <div class="upload__item" v-for="(file, i) in files" :key="file.name">
            <span>{{ file.name }}</span>
            <span class="remove__btn" @click="() => removeFile(i)">
              <Icon name="mdi:close"/>
            </span>
          </div>
        </div>
        <div class="upload__button">
          <input type="file" :id="`${name}_upload`" hidden @change="onUpload" multiple>
          <label :for="`${name}_upload`" class="btn btn--wide btn--accent btn--square mb-2">Selecteer bestanden</label>
        </div>
      </div>
      <Transition name="fade">
        <div class="upload__overlay" v-if="isOverDropZone">
          <Icon name="line-md:uploading-loop" class="upload__icon"/>
          <p class="text--primary text--bold text--l">Laat de bestanden hier los om te uploaden</p>
        </div>
      </Transition>
    </div>
    <template v-if="validator?.$errors">
      <p v-for="(error, i) in validator?.$errors" :key="i" class="field__error">
        {{ error.$message }}
      </p>
    </template>
  </div>
</template>

<script lang="ts" setup>
import type { BaseValidation } from '@vuelidate/core';
import type { UseDropZoneOptions } from '@vueuse/core';
import mime from 'mime';

const props = defineProps({
  label: {
    type: String as PropType<string>,
    default: ''
  },
  name: {
    type: String as PropType<string>,
    default: ''
  },
  extensions: {
    type: Array as PropType<string[]>,
    default: () => []
  },
  modelValue: {
    type: Array as PropType<File[]>,
    default: () => []
  },
  validator: {
    type: Object as PropType<BaseValidation>
  }
});

const emit = defineEmits(['update:modelValue', 'fileError']);

const dropzone = ref<HTMLElement | undefined>();

const files = computed<File[]>({
  get: () => props.modelValue,
  set: (value) => {
    emit('update:modelValue', value);
  }
});

const acceptedMimes = props.extensions.map((ext) => mime.getType(ext) || '');

function addFileToList (file: File) {
  // Check if mime type is accepted
  if (acceptedMimes.length > 0 && !acceptedMimes.includes(file.type)) {
    // Type is not allowed so return and add error
    emit('fileError', {
      message: `Bestandstype van "${file.name}" is niet toegestaan`
    });
  } else {
    files.value.push(file);
  }
}

function onUpload(e: Event) {
  const target = e.target as HTMLInputElement;
  const uploadedFiles: FileList = target.files as FileList;

  if (uploadedFiles) {
    for (const file of uploadedFiles) {
      addFileToList(file);
    }
  }
}

function onDrop (droppedFiles: File[] | null) {
  if (droppedFiles) {
    for (const file of droppedFiles) {
      addFileToList(file);
    }
  }
}

function removeFile (index: number) {
  files.value.splice(index, 1);
}

// Setup dropzone with configuration
const config: UseDropZoneOptions = { onDrop };

if (acceptedMimes && acceptedMimes.length > 0) {
  config.dataTypes = acceptedMimes;
}

const { isOverDropZone } = useDropZone(dropzone, config);
</script>
